import type { VaultEntryKind, VaultReferenceContext, VaultReferenceProvider } from "./vault";
import type { QueryRiskLevel, RestParam } from "./queryMetadata";
import type {
  RuntimeExecutionContext,
  RuntimeExecutionMeta,
  RuntimeExecutionRejection,
} from "./runtimeExecution";
import type {
  CatalogKind,
  QueryOperationConfig,
  RequestType,
  ServerConnectionConfig,
  ServerKind,
} from "./targets";

export interface RuntimeVaultValue {
  kind: VaultEntryKind;
  key: string;
  value: string;
}

export interface RuntimeHealthResponse {
  ok: true;
  version: string;
  paired: boolean;
  agentProviders: string[];
  authenticated: boolean;
  user?: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string | null;
  };
}

export type WorkspaceMode = "anonymous" | "signed-in";

export function normalizeWorkspaceMode(value: string | null | undefined): WorkspaceMode {
  return value === "signed-in" || value === "server" ? "signed-in" : "anonymous";
}

export interface RuntimeWorkspaceQuery {
  id: string;
  name: string;
  queryText: string;
  variables: string;
  headers: string;
  remarks?: string;
  riskLevel?: QueryRiskLevel;
  category?: string;
  pinnedAt?: string | null;
  requestType?: RequestType;
  restMethod?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
  restPath?: string;
  restParams?: RestParam[];
  restContentType?: "json" | "xml" | "text" | "form-urlencoded";
  operationConfig?: QueryOperationConfig;
}

export interface RuntimeWorkspaceServer {
  id: string;
  name: string;
  serverKind?: ServerKind;
  connectionConfig?: ServerConnectionConfig;
  baseUrl: string;
  graphqlPath?: string;
  restPath?: string;
  authType: string;
  authConfig: string;
  isProduction?: boolean;
  timeoutMs?: number;
  sharedConfigId?: string;
  queries: RuntimeWorkspaceQuery[];
  vaultValues: RuntimeVaultValue[];
}

export interface RuntimeWorkspaceSnapshot {
  schemaVersion: 2;
  mode: WorkspaceMode;
  syncedAt: string;
  servers: RuntimeWorkspaceServer[];
}

export interface RuntimeWorkspaceStatusResponse {
  mode: WorkspaceMode;
  syncedAt?: string;
  serverCount: number;
  authenticated?: boolean;
  user?: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string | null;
  };
}

export interface RuntimeWorkspaceSyncRequest {
  snapshot: RuntimeWorkspaceSnapshot;
}

export interface RuntimePairStartResponse {
  code: string;
  expiresAt: string;
}

export interface RuntimePairCompleteRequest {
  code: string;
}

export interface RuntimePairCompleteResponse {
  token: string;
}

export interface RuntimeAuthStatusResponse {
  authenticated: boolean;
  user?: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string | null;
  };
}

export interface RuntimeAuthStartResponse {
  url: string;
}

export interface RuntimeSchemaEntry {
  serverId: string;
  fetchedAt: string;
  catalogKind?: CatalogKind;
  catalogJson?: string;
  introspectionJson: string;
}

export interface RuntimeSchemaStatusResponse {
  serverId: string;
  fetchedAt?: string;
  hasSchema: boolean;
}

export interface RuntimeSchemaRootResponse {
  serverId: string;
  rootTypes: {
    query?: string;
    mutation?: string;
    subscription?: string;
  };
}

export interface RuntimeSchemaSearchResponse {
  serverId: string;
  catalogKind?: CatalogKind;
  results: Array<{
    name: string;
    kind: string;
    description?: string | null;
  }>;
}

export interface RuntimeSchemaTypeResponse {
  serverId: string;
  catalogKind?: CatalogKind;
  type: unknown;
}

export interface RuntimeGraphqlExecuteRequest {
  serverId?: string;
  endpointUrl: string;
  authType: string;
  authConfig?: string | null;
  customHeaders?: Record<string, string>;
  queryText: string;
  variables?: string;
  timeoutMs?: number;
  vaultValues?: RuntimeVaultValue[];
  executionContext?: RuntimeExecutionContext;
}

export interface RuntimeMongoExecuteRequest {
  serverId?: string;
  connectionUri: string;
  database: string;
  collection: string;
  operation: "find" | "findOne" | "aggregate" | "replaceById";
  queryText: string;
  variables?: string;
  timeoutMs?: number;
  vaultValues?: RuntimeVaultValue[];
  executionContext?: RuntimeExecutionContext;
}

export type RuntimeOutputType = "json" | "xml" | "csv" | "html" | "text" | "ndjson" | "unknown";

export interface RuntimeOutputArtifact {
  path: string;
  sizeBytes: number;
  sizeHuman: string;
  contentType: string;
  outputType: RuntimeOutputType;
}

export interface RuntimeMongoExecuteResponse {
  statusCode: number;
  durationMs: number;
  responseText: string;
  riskLevel?: QueryRiskLevel;
  warning?: string;
  data?: unknown;
  errorMessage?: string;
  executionMeta?: RuntimeExecutionMeta;
  rejection?: RuntimeExecutionRejection;
  outputArtifact?: RuntimeOutputArtifact;
  outputWarning?: string;
  outputTruncated?: boolean;
  outputInlineBytes?: number;
}

export interface RuntimeGraphqlExecuteResponse {
  statusCode: number;
  durationMs: number;
  responseText: string;
  responseHeaders: Record<string, string>;
  requestHeaders: Record<string, string>;
  requestBody: string;
  finalUrl: string;
  redirected: boolean;
  riskLevel?: QueryRiskLevel;
  warning?: string;
  data?: unknown;
  errors?: unknown[];
  errorMessage?: string;
  executionMeta?: RuntimeExecutionMeta;
  rejection?: RuntimeExecutionRejection;
  outputArtifact?: RuntimeOutputArtifact;
  outputWarning?: string;
  outputTruncated?: boolean;
  outputInlineBytes?: number;
}

export interface RuntimeRestExecuteRequest {
  serverId?: string;
  url: string;
  method: string;
  authType: string;
  authConfig?: string | null;
  headers?: Record<string, string>;
  body?: string;
  timeoutMs?: number;
  vaultValues?: RuntimeVaultValue[];
  executionContext?: RuntimeExecutionContext;
}

export interface RuntimeRestExecuteResponse {
  statusCode: number;
  durationMs: number;
  responseText: string;
  responseHeaders: Record<string, string>;
  requestHeaders: Record<string, string>;
  requestBody?: string;
  responseContentType: string;
  finalUrl: string;
  redirected: boolean;
  riskLevel?: QueryRiskLevel;
  warning?: string;
  errorMessage?: string;
  executionMeta?: RuntimeExecutionMeta;
  rejection?: RuntimeExecutionRejection;
  outputArtifact?: RuntimeOutputArtifact;
  outputWarning?: string;
  outputTruncated?: boolean;
  outputInlineBytes?: number;
}

export interface RuntimeSqlExecuteRequest {
  serverId?: string;
  connectionUri: string;
  statementText: string;
  paramsJson?: string;
  defaultSchema?: string;
  timeoutMs?: number;
  vaultValues?: RuntimeVaultValue[];
  executionContext?: RuntimeExecutionContext;
}

export interface RuntimeSqlExecuteResponse {
  statusCode: number;
  durationMs: number;
  responseText: string;
  rowCount?: number;
  command?: string;
  rows?: unknown[];
  riskLevel?: QueryRiskLevel;
  warning?: string;
  errorMessage?: string;
  executionMeta?: RuntimeExecutionMeta;
  rejection?: RuntimeExecutionRejection;
  outputArtifact?: RuntimeOutputArtifact;
  outputWarning?: string;
  outputTruncated?: boolean;
  outputInlineBytes?: number;
}

export function createRuntimeVaultProvider(
  values: RuntimeVaultValue[] | undefined,
): VaultReferenceProvider | undefined {
  if (!values?.length) return undefined;

  const valueMap = new Map<string, string>();
  for (const entry of values) {
    valueMap.set(`${entry.kind}:${entry.key}`, entry.value);
  }

  return {
    async resolveValue(kind: VaultEntryKind, key: string, _context?: VaultReferenceContext) {
      const value = valueMap.get(`${kind}:${key}`);
      if (value == null) {
        throw new Error(`Missing runtime vault value: ${kind}:${key}`);
      }
      return value;
    },
  };
}
